/**
 * PlacesTab — Daily work period start/end locations and specific conditions
 * (ferry/train crossings, out-of-scope periods).
 *
 * Data comes from EF_Places (0506) and EF_Specific_Conditions (0522)
 * on the tachograph driver card.
 */

import { DriverAnalysis } from '../types'

export function PlacesTab({ analysis }: { analysis: DriverAnalysis }) {
  const places = analysis.places ?? []
  const conditions = analysis.specific_conditions ?? []

  const hasPlaces = places.length > 0
  const hasConditions = conditions.length > 0

  if (!hasPlaces && !hasConditions) {
    return (
      <div className="text-center py-12">
        <h3 className="text-sm font-semibold text-gray-500">No Location or Condition Data</h3>
        <p className="text-gray-400 text-sm mt-1">
          No place records or specific conditions recorded on this driver card.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Places — daily work period start/end locations */}
      {hasPlaces && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Daily Work Period Locations ({places.length})
            </h3>
          </div>
          <div className="border border-gray-200 rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">Date / Time</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">Type</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">Country</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">Region</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500">Odometer</th>
                </tr>
              </thead>
              <tbody>
                {places.map((place, i) => {
                  const dt = new Date(place.time)
                  const typeLabel = formatPlaceType(place.type)
                  const isBegin = place.type.includes('begin')

                  return (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="py-2 px-3 text-gray-700 whitespace-nowrap">
                        <span className="font-mono text-xs">
                          {dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </span>
                        <span className="text-gray-400 ml-1.5 font-mono text-xs">
                          {dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </td>
                      <td className="py-2 px-3">
                        <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${
                          isBegin ? 'text-green-700' : 'text-blue-700'
                        }`}>
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                            isBegin ? 'bg-green-500' : 'bg-blue-500'
                          }`} />
                          {typeLabel}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-gray-700 text-xs">{place.nation || '—'}</td>
                      <td className="py-2 px-3 text-gray-500 font-mono text-xs">{place.region || '—'}</td>
                      <td className="py-2 px-3 text-right text-gray-600 font-mono text-xs">
                        {place.odometer_km > 0 ? `${place.odometer_km.toLocaleString()} km` : '—'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-[11px] text-gray-400">
            Place records show where daily work periods began and ended, as entered by the driver via the tachograph unit.
            Country and region codes follow the tachograph regulation (EC 1360/2002).
          </p>
        </div>
      )}

      {/* Specific Conditions — ferry/train, out of scope */}
      {hasConditions && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Specific Conditions ({conditions.length})
            </h3>
          </div>
          <div className="border border-gray-200 rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">Date / Time</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">Condition</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500">Code</th>
                </tr>
              </thead>
              <tbody>
                {conditions.map((cond, i) => {
                  const dt = new Date(cond.time)
                  const label = formatConditionType(cond.condition_type)
                  const isScope = cond.condition_type.includes('out_of_scope')
                  const isFerry = cond.condition_type.includes('ferry')

                  return (
                    <tr key={i} className={`border-b border-gray-100 ${
                      isFerry ? 'bg-blue-50/30' : isScope ? 'bg-amber-50/30' : ''
                    }`}>
                      <td className="py-2 px-3 text-gray-700 whitespace-nowrap">
                        <span className="font-mono text-xs">
                          {dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </span>
                        <span className="text-gray-400 ml-1.5 font-mono text-xs">
                          {dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-xs font-medium text-gray-900">{label}</td>
                      <td className="py-2 px-3 font-mono text-xs text-gray-400">0x{cond.condition_code.toString(16).padStart(2, '0').toUpperCase()}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-[11px] text-gray-400">
            Specific conditions record ferry/train crossings (where the vehicle is transported) and out-of-scope periods
            (e.g. driving on private land not covered by EC 561/2006). These affect how driving and rest rules are applied.
          </p>
        </div>
      )}
    </div>
  )
}

function formatPlaceType(type: string): string {
  switch (type) {
    case 'begin': return 'Work Period Begin'
    case 'end': return 'Work Period End'
    case 'begin (daily)': return 'Daily Begin'
    case 'end (daily)': return 'Daily End'
    default: return type
  }
}

function formatConditionType(type: string): string {
  switch (type) {
    case 'out_of_scope_begin': return 'Out of Scope — Begin'
    case 'out_of_scope_end': return 'Out of Scope — End'
    case 'ferry_train_crossing': return 'Ferry / Train Crossing'
    case 'reserved': return 'Reserved'
    default: return type
  }
}
